/* eslint-disable react/prop-types */
import includes from 'lodash/includes';
import merge from 'lodash/merge';
import React from 'react';
import loadable from '@loadable/component';
import ContentAPI from 'api/content';
import EventsAPI from 'api/events';
import App from '../App';
import RouterLoader from './RouterLoader';
import {matchPath} from 'react-router-dom';

const routeOptions = {fallback: <RouterLoader />};
// routes handled in the handleRoute method should be imported here and not inline
// due to issues with spot-details URLs causing a double reload of the page
const Home = loadable(() => import('home/Home'), routeOptions);
const Developers = loadable(
    () => import('developers/Developers'),
    routeOptions
);
const Checkout = loadable(() => import('checkout/Checkout'), routeOptions);
const SpotDetailsAirport = loadable(
    () => import('spot-details/airport/SpotDetailsAirport'),
    routeOptions
);
const SearchMobile = loadable(
    () => import('search/SearchMobile'),
    routeOptions
);
const SearchDesktop = loadable(
    () => import('search/SearchDesktop'),
    routeOptions
);
const SearchAirport = loadable(
    () => import('search/airport/SearchAirport'),
    routeOptions
);
const Category = loadable(() => import('category/Category'), routeOptions);
const NotFound = loadable(() => import('./NotFound'), routeOptions);

export const Page = Object.freeze({
    home: 'home',
    city: 'city',
    destination: 'destination',
    event: 'event',
    airport: 'airport',
    category: 'category',
    notFound: 'not_found',
    redirect: 'redirect',

    isSearch(page) {
        return [this.city, this.destination, this.event, this.airport].includes(
            page
        );
    },

    fromState(page, state) {
        switch (page) {
            case this.city:
                return state.city;
            case this.airport:
            case this.destination:
            case this.event:
                return state.destination;
            case this.category:
            case this.notFound:
            case this.home:
            default:
                return null;
        }
    },
});

function isSearchPage(page, section) {
    return (
        page === 'search' ||
        page === 'spot-details' ||
        includes(page, 'parking-near-me') ||
        includes(section, 'parking-near-me') ||
        (includes(page, '-parking') && !section) ||
        (!includes(page, '-parking') && includes(section, '-parking')) ||
        includes(page, '-monthly-parking')
    );
}

function handleRoute({configData, isMonthly, destination, match}) {
    const {
        params: {page, section},
    } = match;

    if (
        (!section && includes(configData.citySlugs, page)) ||
        isSearchPage(page, section)
    ) {
        if (destination && destination.airport && !isMonthly) {
            return <SearchAirport />;
        }

        return configData.isMobile ? <SearchMobile /> : <SearchDesktop />;
    }

    return <NotFound />;
}

const withNotFound = Component => props =>
    props.pageType === Page.notFound ? <NotFound /> : <Component {...props} />;

const SearchPage = ({configData: {isMobile}}) =>
    isMobile ? <SearchMobile /> : <SearchDesktop />;

const routes = [
    {
        component: App,
        routes: [
            {
                page: Page.home,
                path: '/',
                exact: true,
                component: Home,
                initialLoad: ({initialState, isMobile, path} = {}) => ({
                    promise: ContentAPI.getHomepage(),
                }),
            },
            {
                path: '/developers',
                component: Developers,
                exact: true,
            },
            {
                page: Page.city,
                path: '/city/:city?',
                version: 'v2',
                component: withNotFound(SearchPage),
                exact: true,
            },
            {
                page: Page.destination,
                path: '/destination/:city?/:slug?',
                version: 'v2',
                component: withNotFound(SearchPage),
                exact: true,
            },
            {
                page: Page.airport,
                path: '/airport/:city?/:slug?',
                component: withNotFound(SearchAirport),
                version: 'v2',
                exact: true,
            },
            {
                page: Page.category,
                path: '/category/:city?/:slug?',
                component: withNotFound(Category),
                version: 'v2',
                exact: true,
            },
            {
                path: '/checkout/:spotId/:facilitySlug',
                component: Checkout,
            },
            {
                path: '/airport-parking/:spotId/:facilitySlug',
                component: SpotDetailsAirport,
            },
            {
                path: '/:page/:section?',
                component: handleRoute,
                initialLoad: ({initialState, isMobile, path} = {}) => {
                    const {
                        destination: {
                            data: {
                                id: destinationId,
                                has_upcoming_events: hasEvents,
                            },
                        },
                        searchRequest: {pageType},
                    } = initialState;
                    const info = {
                        promise: Promise.resolve(),
                    };

                    if (!isMobile && pageType !== 'event' && hasEvents) {
                        info.promise = EventsAPI.get({destinationId});
                        info.initialStateModifier = (
                            currentState,
                            loadedData
                        ) => {
                            return merge({}, currentState, {
                                destination: {
                                    data: {
                                        events: loadedData.results,
                                    },
                                },
                            });
                        };
                    }

                    return info;
                },
            },
        ],
    },
];

export function matchPathToRoute(path) {
    for (const route of routes[0].routes) {
        const match = matchPath(path, route);

        if (match) {
            return {...route, params: match.params};
        }
    }

    return null;
}

const possibleBasePages = routes[0].routes
    .filter(route => !route.path.includes(':page'))
    .map(route => route.path.split('/')[1]);

const excludedRoutes = ['logout', 'favicon.ico'];

/**
 * Checks whether the provided path is a valid route on the server that requires loading of APIs and other page setup functionality.
 * This is necessary so that certain initial load paths (like the excluded routes aboveor error routes) don't overload the server with
 * unnecessary API calls. This method cannot check against citySlugs like the handleRoute method does above because we won't have that
 * information on the server to check against (loading it in an API call is not feasible since the city data will potentially become
 * stale if the node server isn't restarted for a long time and re-makes the API call again to get new city slugs).
 * @function isValidRoute
 * @param {String} path - The requested path.
 * @example
 * isValidRoute('/'); // true
 * isValidRoute('/chicago/ohare-airport-parking'); // true
 * isValidRoute('/logout'); // false
 * @returns {Boolean} - Whether the path is a valid route.
 */
export function isValidRoute(path) {
    const [, page, section] = path.split('/');

    return (
        path === '/' || // homepage
        includes(possibleBasePages, page) || // a base route match (/checkout/*, /airport-parking/*, etc)
        isSearchPage(page, section) || // a search page (excluding redirects like /[city] -> /[city]-parking)
        !includes(excludedRoutes, page) // this MUST be the last condition checked
    );
}

export default routes;
